import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { get, isEmpty } from 'lodash';

import MenuItem from '~/components/core/Atomic/MenuItem';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';

import { BOOL4_TYPES, DB_FIELDS_VALID_VALUES, SELECT_TYPES } from '../../constants';
import copies from '../copies.json';

const getConfiguredFieldsPath = (id) => `${id?.split('.')?.slice(0, -1)?.join('.')}.configured_fields`;
const filteringFuncByType = (type) => (field) => field?.type === type;

// contact and location are stored in a fixed set of db columns, so a field of one of those types is
// only valid while one of its type's slots is free. Derived from the declaration rather than kept
// as a parallel count, which is what let the two drift.
const SLOT_BACKED_TYPES = Object.keys(DB_FIELDS_VALID_VALUES);
const isSlotBackedType = (type) => SLOT_BACKED_TYPES.includes(type);
const getAtCapNote = (option) =>
  copies.field_type_at_cap_note
    .replace('{max}', String(DB_FIELDS_VALID_VALUES[option.id].length))
    .replace('{label}', option.desc);

// The slot this field may occupy for `selectedType`: the one it already holds when that is still a
// slot of the type, otherwise the first slot no other field holds. `undefined` means every slot of
// the type is taken - the same condition that keeps the type out of the Type options below, so the
// offer and the assignment cannot disagree.
const getAvailableDbField = ({ fieldsByType, selectedType, currentDbField }) => {
  const slots = DB_FIELDS_VALID_VALUES[selectedType] ?? [];

  if (slots.includes(currentDbField)) {
    return currentDbField;
  }

  const occupiedSlots = (fieldsByType[selectedType] ?? []).map((field) => field?.db_field);
  return slots.find((slot) => !occupiedSlots.includes(slot));
};

const onChangeType = (values, setFieldValue, id, selectedType, fieldsByType, setCurType) => {
  setCurType(selectedType);
  setFieldValue(`${id}.type`, selectedType);
  setFieldValue(`${id}.disable_na`, undefined);
  setFieldValue(`${id}.disable_unknown`, undefined);
  // Resolve db_field in the one statement that writes it. Clearing it first and reading it back
  // from `values` re-read the pre-change snapshot, so a field that already held any slot skipped
  // the assignment and ended up with no db_field at all - the state the server refuses.
  setFieldValue(
    `${id}.db_field`,
    isSlotBackedType(selectedType)
      ? getAvailableDbField({ fieldsByType, selectedType, currentDbField: get(values, `${id}.db_field`) })
      : undefined
  );

  if (!SELECT_TYPES.includes(selectedType)) {
    setFieldValue(`${id}.options`, undefined);
  }

  switch (selectedType) {
    case 'yes_no':
      setFieldValue(`${id}.type`, 'yes_no_na_unknown');
      setFieldValue(`${id}.disable_na`, true);
      setFieldValue(`${id}.disable_unknown`, true);
      break;
    case 'yes_no_na':
      setFieldValue(`${id}.type`, 'yes_no_na_unknown');
      setFieldValue(`${id}.disable_na`, false);
      setFieldValue(`${id}.disable_unknown`, true);
      break;
    case 'yes_no_unknown':
      setFieldValue(`${id}.type`, 'yes_no_na_unknown');
      setFieldValue(`${id}.disable_na`, true);
      setFieldValue(`${id}.disable_unknown`, false);
      break;
    default:
      break;
  }
};

const ConfigurationFieldType = ({ id, isNewConfiguredField = false, excludeTypes = [], disabled = false }) => {
  const { values, setFieldValue, touched, errors } = useFormikContext();
  const [curType, setCurType] = useState(get(values, `${id}.type`, ''));
  const isLimitedToCategories = !isEmpty(get(values, `${id}.incident_sub_type_categories`, []));
  const configuredFieldsPath = getConfiguredFieldsPath(id);
  const configuredFieldsArray = get(values, configuredFieldsPath, []);
  const fieldsByType = Object.fromEntries(
    SLOT_BACKED_TYPES.map((type) => [type, configuredFieldsArray.filter(filteringFuncByType(type))])
  );
  const currentDbField = get(values, `${id}.db_field`);
  const isCurTypeBool4 = BOOL4_TYPES.includes(curType);
  const isDisabled = disabled || (isCurTypeBool4 ? false : !isNewConfiguredField);
  const filterBooleanForNewFields = (option) => (isNewConfiguredField ? option?.id !== 'boolean' : true);
  const filterBool4Fields = (option) =>
    !isNewConfiguredField && isCurTypeBool4 ? BOOL4_TYPES.includes(option?.id) : true;
  // Ask the same helper the type change asks, so a slot-backed type is offered exactly while this
  // field can be given a slot - including the slot it already holds.
  const isOptionDisabled = (option) =>
    isSlotBackedType(option?.id) && !getAvailableDbField({ fieldsByType, selectedType: option.id, currentDbField });

  const options = copies.field_types
    .map((option) => ({
      ...option,
      disabled: isOptionDisabled(option),
    }))
    ?.filter(
      (option) => filterBooleanForNewFields(option) && filterBool4Fields(option) && !excludeTypes.includes(option?.id)
    );

  const fieldError = get(errors, `${id}.type`);
  const fieldTouched = get(touched, `${id}.type`);

  useEffect(() => {
    const savedType = get(values, `${id}.type`, '');
    const disable_na = get(values, `${id}.disable_na`, false);
    const disable_unknown = get(values, `${id}.disable_unknown`, false);

    if (savedType === 'yes_no_na_unknown' && [disable_na, disable_unknown].includes(true)) {
      if (disable_na && !disable_unknown) {
        setCurType('yes_no_unknown');
      } else if (!disable_na && disable_unknown) {
        setCurType('yes_no_na');
      } else if (disable_na && disable_unknown) {
        setCurType('yes_no');
      }
    }
  }, [id, values]);

  return (
    <SingleSelectField
      label="Type"
      value={curType || ''}
      fullWidth
      className={isLimitedToCategories ? 'mt-16' : ''}
      disabled={isDisabled}
      error={fieldError && fieldTouched}
      helperText={fieldError && fieldTouched && fieldError}
      onChange={(selectedType) => {
        onChangeType(values, setFieldValue, id, selectedType, fieldsByType, setCurType);
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.id} value={option.id} disabled={option?.disabled}>
          {option.desc}
          {/* Inline rather than a tooltip: the reason has to reach whoever is choosing, including
              someone arrowing through the list who never hovers the option. */}
          {option?.disabled ? <span className="ml-8 text-slate-700">{getAtCapNote(option)}</span> : null}
        </MenuItem>
      ))}
    </SingleSelectField>
  );
};

ConfigurationFieldType.propTypes = {
  id: PropTypes.string.isRequired,
  isNewConfiguredField: PropTypes.bool,
  excludeTypes: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
};

export default ConfigurationFieldType;
